import { useQuery } from "@tanstack/react-query";
import {
	fetchPageDetail,
	type Period,
} from "@/services/searchPerformanceService";
import { TrendChart } from "@/components/dashboard/TrendChart";
import { IntegrationStatePanel } from "@/components/dashboard/IntegrationStatePanel";

// Sample payload used only when the dashboard's preview flag is on
// (owned by OverviewPage; passed down via the useSampleData prop).
const SAMPLE_DETAIL = { page_url: "", daily_series: [
	{ date: "2026-03-01", impressions: 1580, clicks: 92, ctr: 0.058, position: 6.8 }, { date: "2026-03-02", impressions: 1620, clicks: 98, ctr: 0.06, position: 6.5 },
	{ date: "2026-03-03", impressions: 1490, clicks: 85, ctr: 0.057, position: 6.9 }, { date: "2026-03-04", impressions: 1710, clicks: 105, ctr: 0.061, position: 6.3 },
	{ date: "2026-03-05", impressions: 1650, clicks: 99, ctr: 0.06, position: 6.4 }, { date: "2026-03-06", impressions: 1820, clicks: 118, ctr: 0.065, position: 5.9 },
	{ date: "2026-03-07", impressions: 1390, clicks: 78, ctr: 0.056, position: 7.1 },
], top_queries: [
	{ query: "tankless water heater installation cost", impressions: 2100, clicks: 185, ctr: 0.088, position: 4.2 },
	{ query: "how to install tankless water heater", impressions: 1850, clicks: 142, ctr: 0.077, position: 5.8 },
	{ query: "tankless water heater installation near me", impressions: 1420, clicks: 98, ctr: 0.069, position: 7.3 },
	{ query: "tankless water heater pros and cons", impressions: 980, clicks: 65, ctr: 0.066, position: 9.1 },
], blog_content: null };

export interface PageDetailPanelProps {
	pageUrl: string;
	period: Period;
	tenantId: string | null;
	useSampleData?: boolean;
}

export function PageDetailPanel({
	pageUrl,
	period,
	tenantId,
	useSampleData = false,
}: PageDetailPanelProps) {
	const { data: apiData, isLoading } = useQuery({
		queryKey: ["search-performance", tenantId, "page-detail", pageUrl, period],
		queryFn: () => fetchPageDetail(tenantId, pageUrl, period),
		staleTime: 5 * 60 * 1000,
		enabled: !useSampleData && !!tenantId,
	});

	const data = useSampleData ? SAMPLE_DETAIL : apiData;

	if (isLoading && !useSampleData) return <IntegrationStatePanel state="loading" skeleton="rows" />;
	if (!data) return null;

	return (
		<div className="grid grid-cols-1 gap-6 p-4 lg:grid-cols-2">
			<div>
				<h4 className="mb-3 text-sm font-medium text-foreground">Daily Performance</h4>
				{data.daily_series.length > 0 ? (
					<TrendChart
						data={data.daily_series}
						xKey="date"
						height={200}
						xTickFormatter={(v) => v.slice(5)}
						series={[
							{ dataKey: "clicks", name: "Clicks" },
							{ dataKey: "impressions", name: "Impressions" },
						]}
					/>
				) : (
					<IntegrationStatePanel state="empty" message="No daily data available" />
				)}
			</div>
			<div>
				<h4 className="mb-3 text-sm font-medium text-foreground">Top Queries</h4>
				{data.top_queries.length > 0 ? (
					<div className="max-h-[200px] overflow-auto">
						<table className="w-full text-sm">
							<thead className="sticky top-0 bg-muted">
								<tr className="text-left text-xs text-muted-foreground">
									<th className="pb-2 pr-3">Query</th>
									<th className="pb-2 pr-3 text-right">Impr</th>
									<th className="pb-2 pr-3 text-right">Clicks</th>
									<th className="pb-2 text-right">Pos</th>
								</tr>
							</thead>
							<tbody>
								{data.top_queries.map((q) => (
									<tr key={q.query} className="border-t border-border">
										<td className="max-w-[200px] truncate py-1.5 pr-3">{q.query}</td>
										<td className="py-1.5 pr-3 text-right">{q.impressions.toLocaleString()}</td>
										<td className="py-1.5 pr-3 text-right">{q.clicks.toLocaleString()}</td>
										<td className="py-1.5 text-right">{q.position.toFixed(1)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				) : (
					<IntegrationStatePanel state="empty" message="No query data — run a sync to populate" />
				)}
			</div>
		</div>
	);
}
